import { useState } from 'react';
import { useToggle } from 'react-use';
import {
  Button,
  InputAdornment,
  TextField as MuiTextField,
  TextFieldProps,
  Typography,
} from '@mui/material';
import { FieldHookConfig, useField } from 'formik';

import { FormLabel } from '../label/FormLabel';

type Props = {
  label?: React.ReactNode;
  placeholder?: string;
  defaultHelperText?: string;
  textFieldProps?: TextFieldProps;
  useFormLabel?: boolean;
  onCustomBlur?: (
    event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
  disabled?: boolean;
} & FieldHookConfig<string>;

export const TextField: React.FC<Props> = ({
  label,
  placeholder,
  defaultHelperText,
  onCustomBlur,
  disabled,
  useFormLabel = false, // Default is to use MUI label unless specified
  ...props
}) => {
  const [{ name, value, onChange, onBlur }, { touched, error }] =
    useField(props);

  // State for toggling password visibility
  const [showPassword, toggleShowPassword] = useToggle(false);

  // State for checking if text field is focused
  const [isFocused, setIsFocused] = useState(false);

  const handleClickShowPassword = () => toggleShowPassword();
  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    event.preventDefault();
  };

  return (
    <>
      {useFormLabel && label && <FormLabel label={label} />}

      <MuiTextField
        // Only pass the label to MUI TextField if `useFormLabel` is false
        label={!useFormLabel ? label : undefined}
        fullWidth
        placeholder={placeholder}
        name={name}
        value={value}
        onChange={onChange}
        onBlur={(e) => {
          setIsFocused(false);
          onCustomBlur ? onCustomBlur(e) : onBlur(e);
        }}
        onFocus={() => setIsFocused(true)} // Detect focus event
        disabled={disabled}
        error={!!(touched && error)}
        helperText={(touched && error) || defaultHelperText}
        // Styling label
        InputLabelProps={{
          sx: {
            color:
              touched && error
                ? 'error.main' // Change label color when error
                : isFocused
                ? 'text.highlight' // Change label color on focus
                : 'text.placeholder', // Default label color
          },
        }}
        type={
          props.type !== 'password'
            ? props.type
            : showPassword
            ? 'text'
            : 'password'
        }
        // Toggle between password and text
        InputProps={{
          endAdornment:
            props.type === 'password' ? (
              <InputAdornment position="end">
                <Button
                  aria-label="toggle password visibility"
                  onClick={handleClickShowPassword}
                  onMouseDown={handleMouseDownPassword}
                  sx={{ padding: 0, mr: -1.25 }}
                >
                  <Typography
                    variant="body2"
                    fontWeight="500"
                    color="text.highlight"
                  >
                    {showPassword ? 'Hide' : 'Show'}
                  </Typography>
                </Button>
              </InputAdornment>
            ) : null,
        }}
        {...props.textFieldProps}
      />
    </>
  );
};
